import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useAppDispatch } from 'utils/hooks';
import { realNumber } from 'utils/math_util';

import textNodes from 'domains/text_nodes';

import type { TextNodeEntity } from 'domains/text_nodes';

type PassiveTextAreaProps = {
  textNode: TextNodeEntity;
};

function PassiveTextArea({ textNode }: PassiveTextAreaProps) {
  const { fontFamily, fontSize, horizontalAlignment, textContent, verticalAlignment } = textNode;

  const dispatch = useAppDispatch();
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const textSizeRef = useRef<SVGTextElement>(null);
  const textContainerRef = useRef<SVGSVGElement>(null);
  const activeTextNodeElement = document.querySelector(`#text_node--svg_${textNode.id}`);

  const [previewWidth, setPreviewWidth] = useState(405);
  const [relativeHeight, setRelativeHeight] = useState(100);
  const [textY, setTextY] = useState<number | string>('');
  const [textTransform, setTextTransform] = useState(0.5);
  const [relativeFont, setRelativeFont] = useState<number | undefined>(0);

  // this can just be set once and not calculated each time
  const previewRatio = () => {
    if (
      textNode &&
      activeTextNodeElement &&
      textContainerRef.current &&
      !textContainerRef.current.dataset.previewRatio
    ) {
      const { width: activeNodeWidth } = activeTextNodeElement.getBoundingClientRect();
      const { width: textContainerWidth } = textContainerRef.current.getBoundingClientRect();
      textContainerRef.current.dataset.previewRatio = (
        textContainerWidth / activeNodeWidth
      ).toString();
    }
    return Number(textContainerRef?.current?.dataset.previewRatio) || 1;
  };

  const adjustFontSize = (n: number) => n * previewRatio();

  useEffect(() => {
    // reset the text in the textarea when text node changes
    if (textNode && textAreaRef.current && textNode.textContent) {
      textAreaRef.current.value = textNode.textContent;
    }
  }, [textNode]);

  useEffect(() => {
    if (textNode && textContainerRef.current && textAreaRef.current) {
      const activeTextNodeElement = document.querySelector(`#text_node--svg_${textNode.id}`);
      if (!activeTextNodeElement) return;
      const { width: activeNodeWidth, height: activeNodeHeight } =
        activeTextNodeElement.getBoundingClientRect();
      const { width: containerWidth } = textContainerRef.current.getBoundingClientRect();
      const ratio = containerWidth / activeNodeWidth;
      setPreviewWidth(containerWidth);
      setRelativeHeight(activeNodeHeight * ratio);
      const adjustedFontSize = adjustFontSize(Number(fontSize));

      if (realNumber(adjustedFontSize)) {
        setRelativeFont(adjustedFontSize);
      }
    }
  }, [fontSize, textNode]);

  // figure out the y coordinate of the text depending on the vertical alignment
  useEffect(() => {
    if (textContent && relativeFont && textContainerRef.current && textSizeRef.current) {
      const { height: containerHeight } = textContainerRef.current.getBoundingClientRect();
      const { height: linesHeight } = textSizeRef.current.getBoundingClientRect();
      const diff = containerHeight - linesHeight;
      const lineHeight = relativeFont * 1.25;
      const bumper = lineHeight / 2;
      switch (verticalAlignment) {
        case 'start':
          setTextY(bumper);
          break;
        case 'end':
          setTextY(diff + bumper);
          break;
        case 'middle':
        default:
          setTextY(diff / 2 + bumper);
      }
    }
  }, [textContent, verticalAlignment, relativeFont, relativeHeight]);

  // translate the text depending on the alignment
  useEffect(() => {
    switch (horizontalAlignment) {
      case 'start':
        setTextTransform(0);
        break;
      case 'end':
        setTextTransform(1);
        break;
      case 'middle':
      default:
        setTextTransform(0.5);
    }
  }, [horizontalAlignment]);

  // Autoselect the text in the textarea when the active text node changes
  useEffect(() => {
    if (textAreaRef.current) {
      textAreaRef.current.select();
    }
  }, [textNode.id]);

  function handleTextChange(value: string) {
    dispatch(textNodes.actions.changeActiveTextNode({ textContent: value }));
  }

  return (
    <div className="texttools-item__text-node edit-text">
      <div>
        <h2 className="title">
          <FontAwesomeIcon icon="edit" />
          <span>
            <strong>Type Here to Edit Text</strong>
          </span>
        </h2>
        <textarea ref={textAreaRef} rows={3} onChange={(e) => handleTextChange(e.target.value)} />
      </div>
      <div title="This is an approximation of the actual size of the text. It is provided as a visual aid for small and hard to read text areas.">
        <h2 className="title">
          <FontAwesomeIcon icon="eye" />
          <span>
            <strong>Preview</strong>
          </span>
        </h2>
        <svg
          ref={textContainerRef}
          className="text-example-display"
          style={{
            color: '#000',
            height: `${relativeHeight}px`,
            fontFamily,
            fontSize: relativeFont,
          }}
        >
          <text
            ref={textSizeRef}
            y={textY}
            transform={`translate(${previewWidth * textTransform})`}
          >
            {textContent
              .split(/\n/g)
              .filter((l) => !!l)
              .map((t, i) => (
                <tspan
                  key={`${t.toLowerCase()}_${i}`}
                  x="0"
                  textAnchor={horizontalAlignment}
                  dy={`${i === 0 ? 0.3125 : 1.25}em`}
                >
                  {t}
                </tspan>
              ))}
          </text>
        </svg>
      </div>
    </div>
  );
}

export default PassiveTextArea;
